import React, { useState, useEffect, useContext } from 'react';
import { collection, query, where, getDocs, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from "../../context/AuthContext";
import { Calendar, CheckCircle, Clock, AlertTriangle, ChevronLeft, ChevronRight, User, MapPin, Search, Calendar as CalendarIcon, Filter } from 'lucide-react';
import { fetchVigilantesPorServicio } from "../admin/firestoreService";
import './styles/routeRecordsFilter.scss';

const RouteRecordsFilter = ({ onBack, clientes, servicios }) => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Filtros
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedCliente, setSelectedCliente] = useState('');
    const [selectedServicio, setSelectedServicio] = useState('');
    const [selectedVigilante, setSelectedVigilante] = useState('');
    const [vigilantes, setVigilantes] = useState([]);
    const [selectedEstado, setSelectedEstado] = useState('');
    const [filteredServicios, setFilteredServicios] = useState([]);

    const recordsPerPage = 10;
    const { currentService } = useContext(AuthContext);

    // Cargar usuarios usando firestoreService
    useEffect(() => {
        const fetchVigilantes = async () => {
            try {
                if (currentService) {
                    const vigilantesList = await fetchVigilantesPorServicio(currentService);
                    setVigilantes(vigilantesList);
                }
            } catch (err) {
                console.error('Error al cargar usuarios:', err);
                setVigilantes([]);
            }
        };

        fetchVigilantes();
    }, [currentService]);

    // Filtrar servicios cuando se selecciona un cliente
    useEffect(() => {
        if (selectedCliente) {
            const filtered = servicios.filter(servicio =>
                servicio.cliente === selectedCliente ||
                servicio.clienteId === selectedCliente ||
                servicio.nombreCliente === selectedCliente
            );
            setFilteredServicios(filtered);
        } else {
            setFilteredServicios(servicios);
        }

        // Resetear servicio seleccionado al cambiar el cliente
        setSelectedServicio('');
    }, [selectedCliente, servicios]);

    // Buscar registros con los filtros aplicados
    const fetchFilteredRecords = async () => {
        try {
            setLoading(true);
            setError(null);

            let registrosRef = collection(db, 'servicios', currentService.id, 'registrosRuta');
            let constraints = [];

            // Aplicar filtros de fecha
            if (startDate) {
                const startTimestamp = Timestamp.fromDate(new Date(startDate));
                constraints.push(where('fechaProgramada', '>=', startTimestamp));
            }

            if (endDate) {
                // Ajustar la fecha de fin para incluir todo el día
                const endDateObj = new Date(endDate);
                endDateObj.setHours(23, 59, 59, 999);
                const endTimestamp = Timestamp.fromDate(endDateObj);
                constraints.push(where('fechaProgramada', '<=', endTimestamp));
            }

            // Filtro por vigilante
            if (selectedVigilante) {
                constraints.push(where('vigilanteId', '==', selectedVigilante));
            }

            // Filtro por estado
            if (selectedEstado) {
                constraints.push(where('estado', '==', selectedEstado));
            }

            // Filtro por servicio
            if (selectedServicio) {
                constraints.push(where('servicioId', '==', selectedServicio));
            }

            // Filtro por cliente
            if (selectedCliente) {
                constraints.push(where('clienteId', '==', selectedCliente));
            }

            // Construir la consulta con todos los filtros aplicados
            let recordsQuery;

            if (constraints.length > 0) {
                // Firebase permite máximo un campo de desigualdad, así que usamos la consulta compuesta
                recordsQuery = query(registrosRef, ...constraints, orderBy('fechaProgramada', 'desc'));
            } else {
                // Si no hay filtros, ordenar por fecha
                recordsQuery = query(registrosRef, orderBy('fechaProgramada', 'desc'));
            }

            const querySnapshot = await getDocs(recordsQuery);

            // Convertir a formato utilizable
            const recordsList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                // Convertir timestamps de Firestore a Date si existen
                scheduledTime: doc.data().fechaProgramada?.toDate ? doc.data().fechaProgramada.toDate() : doc.data().fechaProgramada,
                startTime: doc.data().horaInicio?.toDate ? doc.data().horaInicio.toDate() : doc.data().horaInicio,
                endTime: doc.data().horaFin?.toDate ? doc.data().horaFin.toDate() : doc.data().horaFin
            }));

            setRecords(recordsList);

            // Calcular paginación
            setTotalPages(Math.ceil(recordsList.length / recordsPerPage));
            if (recordsList.length === 0) setTotalPages(1);

            setCurrentPage(1);
            setLoading(false);
        } catch (err) {
            console.error('Error al cargar los registros:', err);
            setError('Error al buscar registros. Por favor, inténtalo de nuevo.');
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchFilteredRecords();
    };

    const handleReset = () => {
        setStartDate('');
        setEndDate('');
        setSelectedCliente('');
        setSelectedServicio('');
        setSelectedVigilante('');
        setSelectedEstado('');
        setRecords([]);
        setCurrentPage(1);
        setTotalPages(1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handleViewDetails = (record) => {
        setSelectedRecord(record);
    };

    const handleCloseDetails = () => {
        setSelectedRecord(null);
    };

    // Obtener los registros para la página actual
    const getCurrentPageRecords = () => {
        const startIndex = (currentPage - 1) * recordsPerPage;
        const endIndex = startIndex + recordsPerPage;
        return records.slice(startIndex, endIndex);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
            case 'completada':
                return (
                    <span className="status-badge completed">
                        <CheckCircle size={12} className="icon" />
                        Completada
                    </span>
                );
            case 'in-progress':
            case 'en-progreso':
                return (
                    <span className="status-badge in-progress">
                        <Clock size={12} className="icon" />
                        En Progreso
                    </span>
                );
            case 'missed':
            case 'perdida':
                return (
                    <span className="status-badge missed">
                        <AlertTriangle size={12} className="icon" />
                        Perdida
                    </span>
                );
            default:
                return (
                    <span className="status-badge default">
                        {status}
                    </span>
                );
        }
    };

    const formatDateTime = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString();
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString();
    };

    const formatTime = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleTimeString();
    };

    return (
        <div className="route-records-filter">
            <h2 className="section-title">
                <Search size={20} className="section-icon" />
                Búsqueda de Registros
            </h2>

            <div className="filter-form">
                <form onSubmit={handleSearch}>
                    <div className="filter-grid">
                        <div className="filter-row">
                            <div className="filter-group">
                                <label>Fecha Inicio:</label>
                                <div className="date-input-container">
                                    <CalendarIcon size={16} className="date-icon" />
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="date-input"
                                    />
                                </div>
                            </div>

                            <div className="filter-group">
                                <label>Fecha Fin:</label>
                                <div className="date-input-container">
                                    <CalendarIcon size={16} className="date-icon" />
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="date-input"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="filter-row">
                            <div className="filter-group">
                                <label>Cliente:</label>
                                <select
                                    value={selectedCliente}
                                    onChange={(e) => setSelectedCliente(e.target.value)}
                                    className="select-input"
                                >
                                    <option value="">Todos los clientes</option>
                                    {clientes.map(cliente => (
                                        <option key={cliente.id} value={cliente.id}>
                                            {cliente.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Servicio:</label>
                                <select
                                    value={selectedServicio}
                                    onChange={(e) => setSelectedServicio(e.target.value)}
                                    className="select-input"
                                >
                                    <option value="">Todos los servicios</option>
                                    {filteredServicios.map(servicio => (
                                        <option key={servicio.id} value={servicio.id}>
                                            {servicio.numTienda || servicio.nombre || servicio.descripcion || servicio.id}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="filter-row">
                            <div className="filter-group">
                                <label>Usuario:</label>
                                <select
                                    value={selectedVigilante}
                                    onChange={(e) => setSelectedVigilante(e.target.value)}
                                    className="select-input"
                                >
                                    <option value="">Todos los usuarios</option>
                                    {vigilantes.map(vigilante => (
                                        <option key={vigilante.id} value={vigilante.id}>
                                            {vigilante.displayName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label>Estado:</label>
                                <select
                                    value={selectedEstado}
                                    onChange={(e) => setSelectedEstado(e.target.value)}
                                    className="select-input"
                                >
                                    <option value="">Todos los estados</option>
                                    <option value="completada">Completada</option>
                                    <option value="en-progreso">En Progreso</option>
                                    <option value="perdida">Perdida</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-actions">
                        <button type="submit" className="btn btn-search">
                            <Search size={16} className="btn-icon" />
                            Buscar
                        </button>
                        <button type="button" className="btn btn-reset" onClick={handleReset}>
                            Restablecer
                        </button>
                    </div>
                </form>
            </div>

            {error && (
                <div className="error-message">{error}</div>
            )}

            {loading ? (
                <div className="loading-message">Cargando registros...</div>
            ) : selectedRecord ? (
                <div className="record-details">
                    <div className="details-header">
                        <h4 className="header-title">
                            <Calendar size={18} className="icon" />
                            Detalles de Ronda - {formatDate(selectedRecord.scheduledTime)}
                        </h4>
                        <button
                            onClick={handleCloseDetails}
                            className="back-button"
                        >
                            <ChevronLeft size={20} className="icon" />
                            Volver
                        </button>
                    </div>

                    <div className="details-content">
                        <div className="details-grid">
                            <div className="detail-item">
                                <div className="label">Estado</div>
                                <div className="value">{getStatusBadge(selectedRecord.status || selectedRecord.estado)}</div>
                            </div>

                            <div className="detail-item">
                                <div className="label">Usuario</div>
                                <div className="value with-icon">
                                    <User size={16} className="icon" />
                                    {selectedRecord.nombreVigilante || selectedRecord.guardName || 'Sin asignar'}
                                </div>
                            </div>

                            <div className="detail-item">
                                <div className="label">Programada para</div>
                                <div className="value">{formatDateTime(selectedRecord.scheduledTime)}</div>
                            </div>

                            <div className="detail-item">
                                <div className="label">Duración</div>
                                <div className="value">
                                    {selectedRecord.startTime && selectedRecord.endTime ? (
                                        `${Math.round((selectedRecord.endTime - selectedRecord.startTime) / 60000)} minutos`
                                    ) : 'N/A'}
                                </div>
                            </div>

                            <div className="detail-item">
                                <div className="label">Hora de inicio</div>
                                <div className="value">{formatTime(selectedRecord.startTime)}</div>
                            </div>

                            <div className="detail-item">
                                <div className="label">Hora de finalización</div>
                                <div className="value">{formatTime(selectedRecord.endTime)}</div>
                            </div>
                        </div>

                        <h5 className="checkpoints-title">Puntos Verificados</h5>

                        {(selectedRecord.puntosRegistro || selectedRecord.checkpointRecords)?.length > 0 ? (
                            <div className="checkpoints-list">
                                {(selectedRecord.puntosRegistro || selectedRecord.checkpointRecords).map((checkpoint, index) => (
                                    <div
                                        key={checkpoint.puntoId || checkpoint.checkpointId || index}
                                        className="checkpoint-item"
                                    >
                                        <div className="checkpoint-content">
                                            <div className="checkpoint-info">
                                                <div className="checkpoint-name">Punto {index + 1}</div>
                                                <div className="checkpoint-location">
                                                    <MapPin size={14} className="icon" />
                                                    {checkpoint.puntoId || checkpoint.checkpointId}
                                                </div>
                                            </div>
                                            <div className={(checkpoint.estado || checkpoint.status) === 'checked' ? 'checkpoint-status checked' : 'checkpoint-status not-checked'}>
                                                {(checkpoint.estado || checkpoint.status) === 'checked' ? (
                                                    <>
                                                        <CheckCircle size={16} className="icon" />
                                                        {formatTime(checkpoint.timestamp)}
                                                    </>
                                                ) : (
                                                    'No verificado'
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="empty-checkpoints">No hay registros de puntos de control</p>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="results-container">
                        <h3 className="results-title">
                            <Filter size={18} className="icon" />
                            Resultados
                        </h3>

                        {records.length === 0 ? (
                            <div className="empty-message">
                                No hay registros que coincidan con los filtros seleccionados
                            </div>
                        ) : (
                            <>
                                <div className="records-table-container">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Fecha/Hora</th>
                                                <th>Ruta</th>
                                                <th>Usuario</th>
                                                <th>Estado</th>
                                                <th>Duración</th>
                                                <th className="actions-column">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getCurrentPageRecords().map((record) => (
                                                <tr key={record.id}>
                                                    <td>
                                                        <div className="date-cell">
                                                            <Calendar size={16} className="icon" />
                                                            <div className="date-info">
                                                                <div className="date">{formatDate(record.scheduledTime)}</div>
                                                                <div className="time">{formatTime(record.scheduledTime)}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{record.rutaNombre || record.routeName || 'Sin nombre'}</td>
                                                    <td>
                                                        <div className="guard-cell">
                                                            <User size={16} className="icon" />
                                                            <span className="guard-name">{record.nombreVigilante || record.guardName || 'Sin asignar'}</span>
                                                        </div>
                                                    </td>
                                                    <td>{getStatusBadge(record.status || record.estado)}</td>
                                                    <td className="duration-cell">
                                                        {record.startTime && record.endTime ? (
                                                            `${Math.round((record.endTime - record.startTime) / 60000)} min`
                                                        ) : 'N/A'}
                                                    </td>
                                                    <td>
                                                        <div style={{ textAlign: 'right' }}>
                                                            <button
                                                                onClick={() => handleViewDetails(record)}
                                                                className="details-button"
                                                            >
                                                                Detalles
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="pagination-container">
                                    <div className="page-info">
                                        Página {currentPage} de {totalPages}
                                    </div>
                                    <div className="page-buttons">
                                        <button
                                            onClick={handlePreviousPage}
                                            disabled={currentPage <= 1}
                                            className={currentPage <= 1 ? 'page-button disabled' : 'page-button active'}
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <button
                                            onClick={handleNextPage}
                                            disabled={currentPage >= totalPages}
                                            className={currentPage >= totalPages ? 'page-button disabled' : 'page-button active'}
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default RouteRecordsFilter;